import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FireflyService } from '../../services/firefly.service';
import { of, switchMap, tap, catchError } from "rxjs";
import { ToastService } from '../../services/toast.service';
import { UtilityService } from "../../services/utility.service";
import { LogService } from '../../services/log.service';
import { ServiceIcon } from '../shared/page-header/page-header.component';
import { I18nService } from '../../i18n/i18n.service';

@Component({
  selector: 'app-storyboard3d',
  templateUrl: './storyboard3d.component.html',
  styleUrls: ['./storyboard3d.component.scss'],
  standalone: false
})
export class Storyboard3dComponent implements AfterViewInit, OnDestroy {
  // ヘッダーアイコン設定
  headerIcons: ServiceIcon[] = [
    { src: '/assets/icon_firefly.svg', alt: 'Firefly' },
    { src: '/assets/icon_stock.svg', alt: 'Adobe Stock' }
  ];

  @ViewChild('webglCanvas', { static: true }) canvasRef!: ElementRef;
  @ViewChildren('drawCanvas') drawCanvasRefs!: QueryList<ElementRef<HTMLCanvasElement>>;

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private controls!: OrbitControls;
  private model!: THREE.Object3D | null;
  private animationFrameId: any;

  public prompt: string = '';
  public generatedImages: string[] = [];
  private drawCtxs: CanvasRenderingContext2D[] = [];
  private isDrawing: boolean[] = [];
  private lastX: number[] = [];
  private lastY: number[] = [];

  isObjLoading: boolean = false;
  isCreating: boolean = false;
  progress: number = 0;

  constructor(
    private fireflyService: FireflyService,
    private toastService: ToastService,
    private utilityService: UtilityService,
    private logService: LogService,
    public i18n: I18nService
  ) {
  }

  ngAfterViewInit() {
    this.initThreeJS();
    this.animate();
  }

  private initThreeJS() {
    const canvas = this.canvasRef.nativeElement;
    const parent = canvas.parentElement;
    if (!parent) return;

    const width = parent.clientWidth;
    const height = Math.round(width * (3 / 4));

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xe1e1e1);

    this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    this.camera.position.set(0, 2, 5);

    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5);
    this.scene.add(directionalLight);

    const axesHelper = new THREE.AxesHelper(100);
    this.scene.add(axesHelper);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.screenSpacePanning = false;

    window.addEventListener('resize', this.onWindowResize);
  }

  private animate = () => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  private onWindowResize = () => {
    const parent = this.canvasRef.nativeElement.parentElement;
    if (!parent) return;

    const width = parent.clientWidth;
    const height = Math.round(width * (3 / 4));

    this.renderer.setSize(width, height);
    this.camera.aspect = 4 / 3;
    this.camera.updateProjectionMatrix();
  };

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const url = URL.createObjectURL(file);
    const fileExtension = file.name.toLowerCase().split('.').pop();
    
    console.log(`選択されたファイル: ${file.name}`);
    console.log(`ファイル拡張子: ${fileExtension}`);
    console.log(`ファイルサイズ: ${(file.size / 1024 / 1024).toFixed(2)} MB`);

    if (fileExtension === 'obj') {
      this.loadOBJ(url);
    } else if (fileExtension === 'glb' || fileExtension === 'gltf') {
      this.loadGLTF(url);
    } else if (fileExtension === 'fbx') {
      this.loadFBX(url);
    } else {
      this.toastService.showToast(this.i18n.t('toast.unsupportedModel'), 'error');
    }
  }

  private loadOBJ(url: string) {
    this.isObjLoading = true;
    this.progress = 0;

    const loader = new OBJLoader();
    loader.load(
      url,
      (obj) => {
        this.toastService.showToast(this.i18n.t('toast.objLoaded'), 'success');
        this.setModel(obj);
        this.isObjLoading = false;
      },
      (xhr) => {
        this.progress = Math.round((xhr.loaded / xhr.total) * 100);
      },
      (error) => {
        this.toastService.showToast(this.i18n.t('toast.objLoadFailed'), 'error');
        this.isObjLoading = false;
      }
    );
  }

  private loadGLTF(url: string) {
    this.isObjLoading = true;
    this.progress = 0;

    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        this.toastService.showToast(this.i18n.t('toast.gltfLoaded'), 'success');
        this.setModel(gltf.scene);
        this.isObjLoading = false;
      },
      (xhr) => {
        this.progress = Math.round((xhr.loaded / xhr.total) * 100);
      },
      (_error) => {
        this.toastService.showToast(this.i18n.t('toast.gltfLoadFailed'), 'error');
        this.isObjLoading = false;
      }
    );
  }

  private loadFBX(url: string) {
    this.isObjLoading = true;
    this.progress = 0;

    console.log('FBXLoader初期化');
    const loader = new FBXLoader();
    
    loader.load(
      url,
      (fbx) => {
        console.log('FBX読み込み成功:', fbx);
        console.log('FBXオブジェクト:', fbx);
        console.log('アニメーション数:', fbx.animations?.length || 0);
        
        this.toastService.showToast(this.i18n.t('toast.fbxLoaded'), 'success');
        this.setModel(fbx);
        this.isObjLoading = false;
        
        // メモリ解放
        URL.revokeObjectURL(url);
      },
      (xhr) => {
        if (xhr.lengthComputable) {
          this.progress = Math.round((xhr.loaded / xhr.total) * 100);
          console.log(`FBX読み込み進捗: ${this.progress}%`);
        }
      },
      (error) => {
        console.error('FBX読み込みエラー:', error);
        this.toastService.showToast(this.i18n.t('toast.fbxLoadFailed'), 'error');
        this.isObjLoading = false;
        
        // メモリ解放
        URL.revokeObjectURL(url);
      }
    );
  }

  private setModel(object: THREE.Object3D) {
    if (this.model) {
      this.scene.remove(this.model);
    }

    this.model = object;
    this.scene.add(this.model);

    const box = new THREE.Box3().setFromObject(this.model);
    const size = new THREE.Vector3();
    box.getSize(size);

    let scaleFactor = 1;
    const maxSize = Math.max(size.x, size.y, size.z);

    if (maxSize > 500) {
      scaleFactor = 50 / maxSize; // 超巨大モデルは縮小
    } else if (maxSize > 50) {
      scaleFactor = 5 / maxSize; // 中規模モデル
    } else if (maxSize < 1) {
      scaleFactor = 1 / maxSize; // 小さすぎるモデルを拡大
    }
    this.model.scale.set(scaleFactor, scaleFactor, scaleFactor);

    const center = new THREE.Vector3();
    box.getCenter(center);
    this.model.position.set(-center.x * scaleFactor, -center.y * scaleFactor, -center.z * scaleFactor);

    console.log(`調整後のスケール: ${scaleFactor}`);
    console.log(`モデルの新しい位置: ${this.model.position.x}, ${this.model.position.y}, ${this.model.position.z}`);

    this.model.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.geometry.computeVertexNormals();
      }
    });

    const distance = Math.max(size.x, size.y, size.z) * scaleFactor * 1;
    this.camera.position.set(0, distance, distance);
    this.camera.lookAt(0, 0, 0);
    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }

  generateImage(): any {
    if (!this.renderer || !this.scene || !this.camera) return;

    this.isCreating = true;

    const axesHelpers = this.scene.children.filter(child => child.type === "AxesHelper");
    axesHelpers.forEach(helper => helper.visible = false);

    const originalBackground = this.scene.background;
    this.scene.background = new THREE.Color(0xffffff);

    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    const { width, height } = this.renderer.domElement;

    tempCanvas.width = width;
    tempCanvas.height = height;

    this.renderer.render(this.scene, this.camera);
    tempCtx?.drawImage(this.renderer.domElement, 0, 0);

    const imageDataUrl = tempCanvas.toDataURL('image/png');
    const fileBlob = this.utilityService.dataURLToBlob(imageDataUrl);

    // Captured from the generate-async request body to populate the log's `model` field.
    let requestedModelVersion: string | undefined;

    this.fireflyService.uploadFile(fileBlob).pipe(
      tap(resp => {
        console.log('Structure image upload response:', resp);
      }),
      switchMap(resp => {
        if (!resp || !resp.fileId) {
          this.toastService.showToast(this.i18n.t('toast.imageUploadFailed'), "error");
          return of(null);
        }

        const requestData = {
          contentClass: 'art',
          numVariations: 1,
          seeds: [0],
          promptBiasingLocaleCode: 'ja-JP',
          structure: {
            imageReference: { source: { uploadId: resp.fileId } },
            strength: 100,
          },
          prompt: this.prompt,
          size: { width: 2304, height: 1792 },
          style: {
            presets: ["line_drawing"], // line_drawing // cartoon
            strength: 100
          },
          modelVersion: 'image3',
          visualIntensity: 10,
        };

        requestedModelVersion = (requestData as { modelVersion?: string }).modelVersion;

        return this.fireflyService.generateAsync(requestData);
      }),
      catchError(_error => {
        this.toastService.showToast(this.i18n.t('toast.imageGenRequestFailed'), "error");
        this.isCreating = false;
        this.scene.background = originalBackground;
        axesHelpers.forEach(helper => helper.visible = true);

        return of(null);
      })
    ).subscribe({
      next: (response) => {
        if (!response) {
          this.isCreating = false;
          this.scene.background = originalBackground;
          axesHelpers.forEach(helper => helper.visible = true);
          return;
        }

        if (!response.success || !response.jobId) {
          this.toastService.showToast(this.i18n.t('toast.jobStartFailed'), "error");
          this.isCreating = false;
          this.scene.background = originalBackground;
          axesHelpers.forEach(helper => helper.visible = true);
          return;
        }

        const jobId = response.jobId;
        console.log(`画像生成ジョブが開始されました。JobID: ${jobId}`);

        this.sendGenerateContentLog(requestedModelVersion);

        this.fireflyService.pollJobStatus(jobId).subscribe({
          next: (statusResponse) => {
            if (statusResponse.status === 'succeeded') {
              this.toastService.showToast(this.i18n.t('toast.imageGenComplete'), "success");

              if (statusResponse.result && statusResponse.result.outputs && statusResponse.result.outputs.length > 0) {
                this.generatedImages.unshift(statusResponse.result.outputs[0].image.url);
                setTimeout(() => this.initDrawCanvases(), 100);
              } else {
                this.toastService.showToast(this.i18n.t('toast.imageResultEmpty'), "error");
              }

              this.isCreating = false;
            }

            if (statusResponse.status === 'failed') {
              this.toastService.showToast(this.i18n.t('toast.imageGenFailed', { detail: statusResponse.message || this.i18n.t('toast.unknownError') }), "error");
              this.isCreating = false;
            }
          },
          error: (error) => {
            console.error(`ジョブ監視中にエラーが発生しました:`, error);
            this.toastService.showToast(this.i18n.t('toast.jobMonitorError', { detail: error.message }), "error");
            this.isCreating = false;
          },
          complete: () => {
            this.scene.background = originalBackground;
            axesHelpers.forEach(helper => helper.visible = true);
          }
        });
      },
      error: (error) => {
        this.toastService.showToast(this.i18n.t('toast.imageGenRequestFailedDetail', { detail: error.message }), "error");
        this.isCreating = false;

        this.scene.background = originalBackground;
        axesHelpers.forEach(helper => helper.visible = true);
      }
    });
  }

  /**
   * Reusable so other generation calls on this page can report the same
   * `generate_content` event once wired up.
   */
  private sendGenerateContentLog(modelVersion?: string): void {
    this.logService.logGenerateContent({
      tool: '3_d_to_sketch',
      contentType: 'image',
      model: modelVersion,
      imageCount: 1
    }).subscribe();
  }

  private initDrawCanvases() {
    this.drawCanvasRefs.forEach((canvasRef, index) => {
      const canvas = canvasRef.nativeElement;
      const ctx = canvas.getContext('2d')!;

      canvas.width = 400;
      canvas.height = 300;
      this.drawCtxs[index] = ctx;

      this.isDrawing[index] = false;
      this.lastX[index] = 0;
      this.lastY[index] = 0;

      const image = new Image();
      image.crossOrigin = "anonymous";
      image.src = this.generatedImages[index];

      image.onload = () => {
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      };

      image.onerror = (_err) => {
        this.toastService.showToast(this.i18n.t('toast.imageLoadFailed', { url: image.src }), "error");
      };

      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'black';
    });
  }

  onMouseDown(index: number, event: MouseEvent) {
    this.isDrawing[index] = true;
    const { offsetX, offsetY } = event;
    this.lastX[index] = offsetX;
    this.lastY[index] = offsetY;
  }

  onMouseMove(index: number, event: MouseEvent) {
    if (!this.isDrawing[index]) return;
    const { offsetX, offsetY } = event;
    this.drawCtxs[index].beginPath();
    this.drawCtxs[index].moveTo(this.lastX[index], this.lastY[index]);
    this.drawCtxs[index].lineTo(offsetX, offsetY);
    this.drawCtxs[index].stroke();
    this.lastX[index] = offsetX;
    this.lastY[index] = offsetY;
  }

  onMouseUp(index: number) {
    this.isDrawing[index] = false;
    this.drawCtxs[index].beginPath();
  }

  saveEditedImage(index: number) {
    const link = document.createElement('a');
    link.href = this.drawCanvasRefs.toArray()[index].nativeElement.toDataURL('image/png');
    link.download = `edited_image_${index}.png`;
    link.click();
  }

  resetView() {
    if (this.model) {
      this.scene.remove(this.model);
      this.model = null;
    }
    this.controls.reset();
  }

  setCameraView(view: string) {
    const distance = 12;

    if (view === "front") {
      this.camera.position.set(0, 0, distance);
    } else if (view === "top") {
      this.camera.position.set(0, distance, 0);
    } else if (view === "side") {
      this.camera.position.set(distance, 0, 0);
    }

    this.controls.enableRotate = true;
    this.camera.lookAt(0, 0, 0);
    this.controls.update();
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.animationFrameId);
    this.scene.clear();
    this.renderer.dispose();
    window.removeEventListener('resize', this.onWindowResize);
  }
}
